<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import UserControls from '@/components/UserControls.vue'
  import { vOnClickOutside } from '@vueuse/components'

  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useCourse from '@/composables/useCourse'

  import { UserModule } from '@/store/modules/user'

  const { logo, mcBurger } = useIcons()
  const { logoUrl, isInternalCompanyAdmin, hasValidUser, scrollToTop } = useCommonMixin()
  const { isDesktopDevice } = useBreakpoint()
  const { onLogoutPage } = useCourse()

  const timerId = ref(0)
  const openUserDropdown = ref(false)
  const navBarHeight = ref(0)
  const mainNavBarRef = ref<HTMLElement | null>(null)

  const hasMultipleCompanies = computed(() => {
    return UserModule.companies.length > 1
  })

  const toggleUserDropdown = () => {
    if (timerId.value) window.clearTimeout(timerId.value)
    timerId.value = window.setTimeout(() => {
      openUserDropdown.value = !openUserDropdown.value
    }, 50)
  }

  onMounted(() => {
    if (mainNavBarRef.value) {
      navBarHeight.value = mainNavBarRef.value.offsetHeight
    }
  })
</script>

<template lang="pug">
.main-nav-bar-wrapper
  .main-nav-bar.header-spacing(ref='mainNavBarRef')
    .overview(@click='$emit("toggle-sidebar")')
      .overview-icon
        SVGRenderer(
          :icon='mcBurger',
          height='15px',
          width='25px',
          stroke-color='var(--primary-text-color)',
          :has-hover='false'
        )
      h5(v-if='isDesktopDevice') {{ $t('overview') }}
    router-link(:to='{ path: "/" }', @click.native='scrollToTop')
      img.logo(:src='logoUrl || logo', alt='logo')
    UserControls(
      @toggle-sidebar='$emit("toggle-sidebar")',
      @toggle-user-dropdown='toggleUserDropdown',
      v-if='!onLogoutPage'
    )
    transition(name='menu-slide')
      .user-dropdown(
        v-if='openUserDropdown',
        v-on-click-outside='toggleUserDropdown',
        :style='{ top: `${navBarHeight}px` }'
      )
        template(v-if='hasValidUser')
          router-link.h4(data-cy='route-account-page', to='/account', @click.native='toggleUserDropdown') {{ $t('account.accountSettings') }}
          router-link.h4(
            data-cy='route-company-selection',
            to='/company-selection',
            v-if='hasMultipleCompanies',
            @click.native='toggleUserDropdown'
          ) {{ $t('navigation.mainNav.switchAcademy') }}
          router-link.h4(to='/flags', v-if='isInternalCompanyAdmin', @click.native='toggleUserDropdown') Flags
        router-link.h4(to='/logout', @click.native='toggleUserDropdown') {{ $t('logout') }}
</template>

<style lang="postcss" scoped>
  .main-nav-bar-wrapper {
    @apply ketch-flex ketch-items-center ketch-justify-center ketch-bg-hero-background-color;

    .main-nav-bar {
      @apply ketch-grid ketch-grid-cols-[1fr_auto_1fr] ketch-items-center ketch-gap-c10 ketch-py-c10 ketch-relative;

      .overview {
        @apply ketch-flex ketch-items-center ketch-space-x-c5 ketch-cursor-pointer ketch-text-hero-foreground-color;
      }

      img.logo {
        @apply ketch-max-h-c40 ketch-max-w-[175px] md:ketch-max-h-c60 md:ketch-max-w-c200;
      }

      .user-dropdown {
        @apply ketch-bg-primary-color ketch-text-primary-foreground-color ketch-absolute ketch-z-20;
        @apply ketch-flex ketch-flex-col ketch-w-[240px] ketch-rounded-none ketch-shadow-d-user-dropdown;
        @apply ketch-right-c10 md:ketch-right-c20;

        > * {
          @apply ketch-py-[17px] ketch-px-c25 ketch-border-b ketch-border-gray-700 ketch-border-opacity-10;
          @apply ketch-text-primary-foreground-color hover:ketch-bg-primary-hover-color;
        }
      }
    }
  }
</style>
